/**
 * Das Bild einer Nadel — für die Karte der Anwendung **und** für die des Buches.
 *
 * **Ein Kreis von vierzehn Pixeln geht auf einer Karte unter** — aus dem
 * Betrieb gemeldet, und auf dem Bildschirmfoto war er tatsächlich kaum zu
 * finden. Also die Form, die jeder von einer Karte kennt: ein Tropfen mit
 * Spitze, weiß umrandet und mit Schatten, damit er auf Wald wie auf Wasser
 * steht.
 *
 * Ein Entwurf bekommt ein Loch in der Mitte. Das ist die Unterscheidung, die
 * ohne Farbe auskommt — wer schlecht sieht oder auf einer grellen Karte
 * schaut, erkennt „noch nicht fertig" an der Form.
 *
 * **Und eine Nummer, wenn eine mitkommt.** Die braucht das Buch: dort ruft der
 * Abschnitt „Die Punkte auf der Karte" jeden Punkt bei seiner Zahl auf, und
 * ohne sie auf der Karte wäre die Zahl daneben eine Behauptung. Die Karte der
 * Anwendung nummeriert nicht — dort tippt man an, statt nachzuschlagen.
 *
 * **Warum diese Datei und nicht zwei Kopien.** Das Buch hatte eine eigene,
 * und die bestand aus der nackten Zahl in einem `<div>` ohne eine einzige Regel
 * dazu: `buch__nadel` stand im ganzen Baum nur an dieser Stelle. Am Gerät
 * standen deshalb Ziffern auf der Karte und keine Nadeln. Was es nur einmal
 * gibt, kann nicht auseinanderlaufen.
 *
 * Der Titel geht **nicht** hier hinein, sondern als Attribut an Leaflet: er
 * ist Text von Menschen, und Text von Menschen wird nicht zu Markup. Die
 * Nummer ist eine Zahl und keiner.
 */
export function nadelbild(
  klassen: string,
  teil: { hohl?: boolean; nummer?: number; bild?: string | null; kennung?: number } = {},
): string {
  const nummer = teil.nummer
  const kennung = teil.kennung === undefined ? '' : ` data-highlight="${teil.kennung}"`
  /*
   * **Mit Foto: ein rundes Bild mit Spitze statt des Tropfens** (#91).
   *
   * Aus dem Betrieb: „Kartenpins enthalten Fotos -- wie in den Kärtchen."
   * Das Vorschaubild ist dasselbe wie auf der Kachel im Stapel
   * (`standbildAdresse`), und es kommt über `/api/media/{id}/thumb`, also
   * durch `Visibility` -- eine Nadel zeigt nichts, was das Kärtchen nicht
   * auch zeigte.
   *
   * Der Rahmen trägt die Farbe, die sonst der Tropfen hat (`--nadel` und
   * die drei Abwandlungen), die Spitze darunter ebenso: die Zustände
   * bleiben an der Farbe erkennbar, und `kartenfarben.mjs` liest weiter
   * `.nadel path`. Ein Punkt ohne Foto behält den Tropfen; das Buch
   * (`nummer`) ebenfalls -- gedruckt zählt die Ziffer, nicht das Bild.
   *
   * `alt=""` und `draggable="false"`: das Bild ist Schmuck der Nadel, ihren
   * Namen trägt der Marker (`title`), und ein ziehbares Bild ließe den
   * Browser beim Wischen das Foto statt der Karte greifen.
   */
  if (teil.bild !== undefined && teil.bild !== null && nummer === undefined) {
    return (
      `<span class="${klassen} nadel--foto"${kennung}>` +
      `<span class="nadel__rahmen"><img src="${teil.bild}" alt="" draggable="false" loading="lazy" decoding="async"></span>` +
      '<svg class="nadel__spitze" viewBox="0 0 14 9" aria-hidden="true"><path d="M0 0L7 9L14 0"/></svg>' +
      '</span>'
    )
  }
  return (
    `<span class="${klassen}"${kennung}` +
    // **Wie viele Stellen** — damit eine dreistellige Zahl kleiner gesetzt
    // werden kann. Der Kopf des Tropfens ist neunzehn Punkte breit; „100" in
    // derselben Größe wie „7" stünde über den Rand hinaus.
    (nummer === undefined ? '' : ` data-stellen="${String(nummer).length}"`) +
    '>' +
    '<svg viewBox="0 0 28 38" aria-hidden="true">' +
    '<path d="M14 1.6C7.4 1.6 2.6 6.6 2.6 12.8c0 8.3 9.9 22.2 11.4 24.2 1.5-2 11.4-15.9 11.4-24.2 0-6.2-4.8-11.2-11.4-11.2z"/>' +
    (teil.hohl === true ? '<circle cx="14" cy="12.6" r="4.4"/>' : '') +
    // **Die Grundlinie steht ausgeschrieben da, nicht als
    // `dominant-baseline`.** Das Merkmal setzen nicht alle Druckmaschinen
    // gleich um, und ein Buch wird gedruckt; 17,4 setzt die Ziffer bei
    // Schriftgröße 13 auf die Mitte des Kopfes (12,6).
    (nummer === undefined
      ? ''
      : `<text x="14" y="17.4" text-anchor="middle">${nummer}</text>`) +
    '</svg></span>'
  )
}
